import type { CoverageBlock, DeploymentManifest } from '../api/contract.ts'
import { NOT_REPORTED, type NotReported } from './heartbeat.ts'
import { fixtureResponse } from '../api/fixtures.ts'
import type { DeploymentRecord } from '../../../api/index.ts'

/**
 * The derived signals on Fleet Overview, and the reasons they are separate.
 *
 * CONSOLES.md 2.1 gives Last seen its own sentence: it is the most important cell on the
 * screen, because silence is the primary failure signal in a deployment-initiated
 * architecture. Nothing reaches into a customer tenant to ask whether a relay is alive, so
 * "we have not heard from you" is the ONLY evidence there is.
 */

const MANIFEST = fixtureResponse<DeploymentManifest>('manifest.200.json')

/**
 * The staleness window, DERIVED from the contract rather than chosen.
 *
 * `pollIntervalSeconds` and `pollJitterSeconds` are fields on the manifest every relay reads,
 * so one poll may legitimately arrive as late as interval + jitter. A deployment is therefore
 * not late until it has missed two of those, and not unreachable until it has missed four.
 * Writing 12 minutes here instead would be a number that stops being true the day somebody
 * changes the poll interval in the contract, and nothing would go red.
 *
 * `STALE_AFTER_S`/`UNREACHABLE_AFTER_S` grade the LEGACY channel only (`lastSeenAt`). The new
 * Relay's `POST /relay/heartbeat` is graded on a different cycle count by console-api
 * (`heartbeat_liveness`) and arrives here pre-graded as `relayHeartbeat.liveness` - see
 * `healthOf`.
 */
export const POLL_WINDOW_S = MANIFEST.pollIntervalSeconds + MANIFEST.pollJitterSeconds
export const STALE_AFTER_S = POLL_WINDOW_S * 2
export const UNREACHABLE_AFTER_S = POLL_WINDOW_S * 4

/** CONSOLES.md 2.1, verbatim: healthy, degraded, unreachable, never-enrolled. */
export type DeploymentHealth = 'healthy' | 'degraded' | 'unreachable' | 'never-enrolled'

export interface HealthVerdict {
  state: DeploymentHealth
  /** Seconds since we last heard anything. Null when we never have. */
  ageSeconds: number | null
  /** Why it is not healthy, in the order a person would act on it. Empty when it is. */
  reasons: string[]
}

export function ageSeconds(lastSeenAt: string | null, now: Date): number | null {
  if (!lastSeenAt) return null
  return Math.max(0, Math.round((now.getTime() - Date.parse(lastSeenAt)) / 1000))
}

/**
 * Liveness and drift. Coverage is NOT folded in, and that is the point.
 *
 * NthAI.md: freshness, confidence and coverage are three signals, not one. A deployment can
 * be perfectly healthy and have a connector sitting at 2% coverage, and a board that averaged
 * those into one colour would show green for the exact failure the coverage feature exists to
 * catch. So coverage gets its own column, beside this one, never blended into it.
 *
 * TWO LIVENESS CHANNELS, AND THE NEW ONE WINS WHEN IT HAS EVER SPOKEN. `relayHeartbeat`
 * (`POST /relay/heartbeat`) is the new Relay's channel; `lastSeenAt` is the old relay's
 * (`cc_deployment.last_heartbeat_at`). The new Relay is replacing the old one, so once a
 * deployment's Relay has ever sent a heartbeat, that channel is the liveness source and the
 * legacy one is not consulted again, even if it is also populated. `never-enrolled` is
 * therefore reserved for the case NEITHER channel has ever reported - see `never-enrolled`
 * below, reached only when `relayHeartbeat` is absent AND `lastSeenAt` is null.
 *
 * `relayHeartbeat.liveness` is graded BY console-api (`heartbeat_liveness`, a different cycle
 * from the poll window below) and read here verbatim rather than re-derived: recomputing a
 * second staleness threshold client-side against the wrong cadence is exactly how the console
 * and console-api come to disagree about the same deployment.
 */
export function healthOf(d: DeploymentRecord, now: Date): HealthVerdict {
  const relay = d.relayHeartbeat
  const legacyAge = ageSeconds(d.lastSeenAt, now)

  if (!relay && legacyAge === null) {
    return {
      state: 'never-enrolled',
      ageSeconds: null,
      reasons: ['Enrolled, and has never called home. Not the same thing as unreachable.'],
    }
  }

  // Drift, policy and connector findings are independent of which channel proves liveness, so
  // they are computed once and either channel's silence reason is placed ahead of them.
  const reasons: string[] = []
  const drift = d.lastState?.driftCount ?? 0
  if (drift > 0) reasons.push(`${drift} definitions drifted from the snapshot we published.`)

  const denials = d.lastState?.estate.policyDenials ?? []
  if (denials.length > 0) reasons.push(`${denials.length} Azure Policy denials in their estate.`)

  if (d.lastState?.estate.subscriptionChanged) reasons.push('Their subscription changed under the deployment.')

  const failing = d.connectors.filter((c) => c.failureCount > 0)
  if (failing.length > 0) reasons.push(`${failing.length} connector instances reporting failures.`)

  if (relay) {
    const age = ageSeconds(relay.receivedAt, now)
    if (relay.liveness === 'unreachable') {
      return {
        state: 'unreachable',
        ageSeconds: age,
        reasons: [`Relay heartbeat silent for ${formatAge(age)}.`],
      }
    }
    if (relay.liveness === 'stale') {
      reasons.unshift(`Relay heartbeat silent for ${formatAge(age)}.`)
    }
    // `running`: fall through to the findings above exactly as today's legacy path does.
    return { state: reasons.length > 0 ? 'degraded' : 'healthy', ageSeconds: age, reasons }
  }

  // No relay heartbeat has ever arrived: today's behaviour on the legacy channel, unchanged.
  const age = legacyAge as number
  if (age >= UNREACHABLE_AFTER_S) {
    return {
      state: 'unreachable',
      ageSeconds: age,
      reasons: [`Silent for ${formatAge(age)}, which is more than four poll windows.`],
    }
  }
  if (age >= STALE_AFTER_S) {
    reasons.unshift(`Silent for ${formatAge(age)}, which is more than two poll windows.`)
  }

  return { state: reasons.length > 0 ? 'degraded' : 'healthy', ageSeconds: age, reasons }
}

/**
 * True once the board should show this deployment as late. wf-04 E-02 is exactly this line.
 *
 * Mirrors `healthOf`'s channel choice: a deployment whose Relay has ever sent a heartbeat is
 * judged on that heartbeat's server-graded liveness (anything short of `running`), never on
 * `STALE_AFTER_S` against the legacy timestamp, which is a different cadence.
 */
export function isStale(d: DeploymentRecord, now: Date): boolean {
  const relay = d.relayHeartbeat
  if (relay) return relay.liveness !== 'running'
  const age = ageSeconds(d.lastSeenAt, now)
  return age !== null && age >= STALE_AFTER_S
}

/**
 * The timestamp "last seen" should render, following the same channel choice as `healthOf`.
 * `formatStamp` for the hover title and `ageSeconds` for the headline text must read the same
 * clock, or a cell can say "2m ago" while its title still shows the old channel's stale stamp.
 */
export function lastSeenStamp(d: DeploymentRecord): string | null {
  return d.relayHeartbeat?.receivedAt ?? d.lastSeenAt
}

// ---------------------------------------------------------------------------
// Versions and snapshots
// ---------------------------------------------------------------------------

export type SnapshotLag = 'current' | 'behind' | 'ahead' | 'held' | 'unknown'

/**
 * Behind, current, ahead, or HELD. CONSOLES.md 2.1 says ahead is a bug, so it renders as one -
 * which is exactly why `held` has to be checked first.
 *
 * A deployment held at N-2 while the fleet publishes on to N matches neither
 * `librarySnapshotId` nor `previousSnapshotId`, so it fell through the three comparisons below
 * to `ahead`, and the board renders that in RED as "ahead · bug". An operator who had just
 * reverted a tenant would have been told the console did not understand what they did - a
 * deliberate act reported as a defect, on the main screen.
 *
 * `hold !== null` is one check and needs no arithmetic, which is why console-api serves a
 * nested object rather than four flat fields. A held deployment is CURRENT relative to its own
 * target while being numerically below the fleet's newest, and those are different questions.
 */
export function snapshotLag(d: DeploymentRecord): SnapshotLag {
  // FIRST, and deliberately before anything is compared. A hold is a fact we were told; the
  // rest of this function infers, and an inference must not override a statement.
  if (d.hold) return 'held'

  // DEPLOYMENT.STATE where the mock has one, else what console-api says the deployment reported.
  const held = d.lastState?.snapshotId ?? d.reportedSnapshotId
  if (!held) return 'unknown'
  if (held === d.librarySnapshotId) return 'current'
  if (held === d.previousSnapshotId) return 'behind'
  // Neither the current snapshot nor the one before it. We did not publish what they are
  // running, which is the case CONSOLES.md calls a bug rather than a state.
  return 'ahead'
}

/**
 * Drift, or the fact that we have not been told any.
 *
 * `lastState` is null until a deployment sends its first DEPLOYMENT.STATE, and a drift count
 * read off a null state is not a zero - it is an absence. `?? 0` here renders "0 definitions
 * have drifted", which is a positive claim of health about a deployment that has never said
 * anything, and it is the claim a never-enrolled deployment is least entitled to.
 *
 * The pressure that produces the bug is worth naming because it will come back: a cell
 * labelled Drift is numeric, a numeric cell wants a number, and `?? 0` is the shortest way to
 * get one. The sibling cells on the same panel resisted it - `held ?? 'never reported'` and
 * `observedAt ?? null` - so absent was handled correctly for the string and the timestamp and
 * lost only where the type was a number.
 *
 * THERE ARE TWO WAYS TO HAVE NO DRIFT COUNT and both are absences, which is why this checks
 * the value rather than the state. `lastState` is null until the first DEPLOYMENT.STATE
 * arrives, AND `driftCount` is itself optional on that message - a deployment can report its
 * state and still not report a count. Only the second was written down; the first is the one
 * a null-check alone would catch. TypeScript found the gap when this function was first
 * written against `lastState` only.
 *
 * A deployment that HAS reported and genuinely has zero drift returns 0, and that 0 is a real
 * answer that must survive: `mock-fleet-api` sets `driftCount: 0` on a successful apply, so
 * an earned zero and an absence sit side by side in the same fleet.
 */
export function driftReported(d: DeploymentRecord): number | NotReported {
  const n = d.lastState?.driftCount
  return typeof n === 'number' ? n : NOT_REPORTED
}

/**
 * The version the Relay reports for its own `relay-service` component, through console-api.
 * NOT `lastState.components`, which is the legacy fleet-api DEPLOYMENT.STATE and stays empty for
 * a deployment running the new Relay: reading it rendered the empty placeholder beside a reported version (FC-2).
 */
export function relayVersion(d: DeploymentRecord): string | null {
  return d.relayComponents.find((c) => c.producer === 'relay-service')?.version ?? null
}

export function relayIsPinned(d: DeploymentRecord): boolean {
  const v = relayVersion(d)
  return v !== null && v === d.pinnedRelayVersion
}

// ---------------------------------------------------------------------------
// Coverage: one number, honest, and never alone
// ---------------------------------------------------------------------------

export interface WorstCoverage {
  instanceKey: string
  overallPct: number
  /**
   * D24: overallPct is NEVER rendered without worstRelevantEntity beside it. A flat rollup
   * read 93.5% on the 15 August state while the critical source sat at 2%.
   */
  worstEntityKey: string
  worstEntityPct: number
  /** D25: an unknown denominator must never default to complete. */
  tier: CoverageBlock['entities'][number]['coverageTier']
  gap: string | null
}

/** The lowest coverage of any connector instance on this deployment. CONSOLES.md 2.1. */
export function worstCoverage(d: DeploymentRecord): WorstCoverage | null {
  let worst: WorstCoverage | null = null

  for (const c of d.connectors) {
    const block = c.coverage
    const entity =
      block.worstRelevantEntity ??
      [...block.entities].sort((a, b) => a.coveragePct - b.coveragePct)[0] ??
      null
    if (!entity) continue

    const candidate: WorstCoverage = {
      instanceKey: block.instanceKey,
      overallPct: block.overallPct,
      worstEntityKey: entity.key,
      worstEntityPct: entity.coveragePct,
      tier: entity.coverageTier,
      gap: block.entities.find((e) => e.key === entity.key)?.gap ?? null,
    }
    if (!worst || candidate.worstEntityPct < worst.worstEntityPct) worst = candidate
  }

  return worst
}

/**
 * How coverage should read as a signal. Never green below complete, and never green at all
 * on an unknown tier: a source with no obtainable denominator is not a source that is fine.
 */
export function coverageTone(c: WorstCoverage | null): 'success' | 'warning' | 'danger' | 'idle' {
  if (!c) return 'idle'
  if (c.tier === 'unknown') return 'warning'
  if (c.worstEntityPct >= 99) return 'success'
  if (c.worstEntityPct >= 90) return 'warning'
  return 'danger'
}

export function healthTone(state: DeploymentHealth): 'success' | 'warning' | 'danger' | 'idle' {
  switch (state) {
    case 'healthy':
      return 'success'
    case 'degraded':
      return 'warning'
    case 'unreachable':
      return 'danger'
    case 'never-enrolled':
      return 'idle'
  }
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function formatAge(seconds: number | null): string {
  if (seconds === null) return 'never'
  if (seconds < 60) return `${seconds}s ago`
  const mins = Math.floor(seconds / 60)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ${mins % 60}m ago`
  return `${Math.floor(hours / 24)}d ago`
}

export function formatStamp(iso: string | null): string {
  if (!iso) return '—'
  return iso.replace('T', ' ').replace(/\.\d+Z$/, 'Z')
}

/** Credential expiry carries a warning inside 30 days (CONSOLES.md 2.2). */
export function credentialWarning(expiresAt: string | null, now: Date): string | null {
  if (!expiresAt) return null
  // Whole days counted AWAY from now in both directions. One `Math.floor` over the signed
  // difference rounded an expiry an hour ago down to -1, "Expired 1 days ago", and one 25 hours
  // ago to "2 days": every past value was a day too far.
  const remaining = Date.parse(expiresAt) - now.getTime()
  if (Number.isNaN(remaining)) return null
  if (remaining <= 0) {
    const days = Math.floor(-remaining / 86_400_000)
    const when = days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'} ago`
    return `Expired ${when}. The deployment cannot call home.`
  }
  const days = Math.floor(remaining / 86_400_000)
  if (days > 30) return null
  const when = days === 0 ? 'within a day' : `in ${days} day${days === 1 ? '' : 's'}`
  return `Expires ${when}. Rotation is ours to run (D16).`
}
